import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  UtensilsCrossed, Calendar, ChevronLeft, ChevronRight, Save,
  Plus, Edit2, Trash2, Download, Sparkles, RefreshCw, Check,
  AlertCircle, CheckCircle2, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const CACFP_COMPONENTS = ['milk', 'protein', 'grains', 'fruits', 'vegetables'];

const CACFP_LABELS = {
  milk: 'Milk',
  protein: 'Meat/Protein',
  grains: 'Grains',
  fruits: 'Fruits',
  vegetables: 'Vegetables'
};

const CACFP_COLORS = {
  milk: 'bg-blue-100 text-blue-700 border-blue-200',
  protein: 'bg-red-100 text-red-700 border-red-200',
  grains: 'bg-amber-100 text-amber-700 border-amber-200',
  fruits: 'bg-pink-100 text-pink-700 border-pink-200',
  vegetables: 'bg-green-100 text-green-700 border-green-200'
};

const CACFP_REQUIREMENTS = {
  breakfast: 3,
  am_snack: 2,
  lunch: 5,
  pm_snack: 2,
  dinner: 5
};

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', req: 3 },
  { id: 'am_snack', label: 'AM Snack', req: 2 },
  { id: 'lunch', label: 'Lunch', req: 5 },
  { id: 'pm_snack', label: 'PM Snack', req: 2 },
  { id: 'dinner', label: 'Dinner', req: 5 }
];

function getMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = -2; i <= 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    opts.push({ value: val, label });
  }
  return opts;
}

function countFilledComponents(components) {
  if (!components) return 0;
  return CACFP_COMPONENTS.filter(c => components[c] && components[c].trim()).length;
}

function isCompliant(mealType, components) {
  return countFilledComponents(components) >= CACFP_REQUIREMENTS[mealType];
}

const MenuPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'provider';

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Month selection
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);

  // Menu state
  const [monthlyMenu, setMonthlyMenu] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [allMenus, setAllMenus] = useState([]);

  // Current week view within month
  const [weekIndex, setWeekIndex] = useState(0);

  // Generate dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateMonth, setGenerateMonth] = useState(currentMonthStr);
  const [dietaryNotes, setDietaryNotes] = useState('');

  // Edit meal dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { dayIndex, mealType }
  const [editForm, setEditForm] = useState({
    name: '',
    components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' }
  });

  useEffect(() => {
    fetchMenuForMonth();
  }, [selectedMonth]);

  const fetchMenuForMonth = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/menu/monthly?month=${selectedMonth}`, getAuthHeaders());
      if (res.data.length > 0) {
        const menu = res.data[0];
        setMonthlyMenu(menu);
        setIsSaved(true);
        // Jump to the week containing today (or the first week if current month isn't selected)
        const todayIso = new Date().toISOString().split('T')[0];
        const days = menu.days || [];
        // Group the same way getWeeks() does
        const weekGroups = [];
        let current = [];
        for (const d of days) {
          current.push(d);
          if (d.day_name === 'Friday' || d === days[days.length - 1]) {
            weekGroups.push(current);
            current = [];
          }
        }
        const idx = weekGroups.findIndex(w => w.some(d => d.date === todayIso));
        setWeekIndex(idx >= 0 ? idx : 0);
      } else {
        setMonthlyMenu(null);
        setIsSaved(false);
      }
    } catch (error) {
      console.error('Failed to fetch monthly menu', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMenus = async () => {
    try {
      const res = await axios.get(`${API}/menu/monthly`, getAuthHeaders());
      setAllMenus(res.data);
    } catch (error) {
      console.error('Failed to fetch menus', error);
    }
  };

  // Generate state
  const [generateProgress, setGenerateProgress] = useState('');

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setShowGenerateDialog(false);
      setGenerateProgress('Preparing monthly menu...');

      // Step 1: Get month metadata (number of weeks)
      const metaRes = await axios.post(`${API}/menu/generate-monthly`, {
        month: generateMonth,
        metadata_only: true
      }, getAuthHeaders());

      const { total_weeks, month_name, year, weeks_info } = metaRes.data;
      const allDays = [];

      // Step 2: Generate each week sequentially
      for (let w = 1; w <= total_weeks; w++) {
        setGenerateProgress(`Generating Week ${w} of ${total_weeks}...`);
        try {
          const weekRes = await axios.post(`${API}/menu/generate-monthly`, {
            month: generateMonth,
            week_number: w,
            dietary_notes: dietaryNotes
          }, getAuthHeaders());
          allDays.push(...weekRes.data.days);
        } catch (err) {
          console.error(`Week ${w} generation failed:`, err);
          // Add empty days for failed week
          if (weeks_info[w - 1]) {
            for (const d of weeks_info[w - 1].days) {
              allDays.push({
                date: d.date,
                day_name: d.day_name,
                meals: {
                  breakfast: { name: '', components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' } },
                  am_snack: { name: '', components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' } },
                  lunch: { name: '', components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' } },
                  pm_snack: { name: '', components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' } },
                  dinner: { name: '', components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' } }
                }
              });
            }
          }
        }
      }

      // Step 3: Assemble the full monthly menu
      const menu = {
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        title: `${month_name || generateMonth} ${year || ''} Menu`.trim(),
        month: generateMonth,
        days: allDays.sort((a, b) => a.date.localeCompare(b.date)),
        status: 'draft',
        generated_by_ai: true,
        dietary_notes: dietaryNotes
      };

      setMonthlyMenu(menu);
      setIsSaved(false);
      setSelectedMonth(generateMonth);
      setWeekIndex(0);
      setGenerateProgress('');
      toast.success('Monthly menu generated! Review and edit, then save.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate menu');
      setGenerateProgress('');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!monthlyMenu) return;
    try {
      setLoading(true);
      await axios.post(`${API}/menu/monthly`, monthlyMenu, getAuthHeaders());
      setIsSaved(true);
      toast.success('Monthly menu saved!');
    } catch (error) {
      toast.error('Failed to save menu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!monthlyMenu?.id || !confirm('Delete this monthly menu?')) return;
    try {
      await axios.delete(`${API}/menu/monthly/${monthlyMenu.id}`, getAuthHeaders());
      setMonthlyMenu(null);
      setIsSaved(false);
      toast.success('Menu deleted');
    } catch (error) {
      toast.error('Failed to delete menu');
    }
  };

  const handleExportPDF = async () => {
    if (!monthlyMenu?.id || !isSaved) {
      toast.error('Please save the menu first to export');
      return;
    }
    try {
      setLoading(true);
      const response = await axios({
        method: 'GET',
        url: `${API}/menu/monthly/${monthlyMenu.id}/export-pdf`,
        headers: getAuthHeaders().headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `menu_${monthlyMenu.month}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

  // Edit meal
  const openEditMeal = (dayIndex, mealType) => {
    const day = monthlyMenu.days[dayIndex];
    const meal = day?.meals?.[mealType] || { name: '', components: { milk: '', protein: '', grains: '', fruits: '', vegetables: '' } };
    setEditTarget({ dayIndex, mealType });
    setEditForm({
      name: meal.name || '',
      components: {
        milk: meal.components?.milk || '',
        protein: meal.components?.protein || '',
        grains: meal.components?.grains || '',
        fruits: meal.components?.fruits || '',
        vegetables: meal.components?.vegetables || ''
      }
    });
    setShowEditDialog(true);
  };

  const saveEditMeal = () => {
    if (!editTarget || !monthlyMenu) return;
    const updated = { ...monthlyMenu };
    const days = [...updated.days];
    const day = { ...days[editTarget.dayIndex] };
    const meals = { ...day.meals };
    meals[editTarget.mealType] = { ...editForm };
    day.meals = meals;
    days[editTarget.dayIndex] = day;
    updated.days = days;
    setMonthlyMenu(updated);
    setIsSaved(false);
    setShowEditDialog(false);
    toast.success('Meal updated');
  };

  // Group days into weeks
  const getWeeks = () => {
    if (!monthlyMenu?.days) return [];
    const weeks = [];
    let current = [];
    for (const day of monthlyMenu.days) {
      current.push(day);
      if (day.day_name === 'Friday' || day === monthlyMenu.days[monthlyMenu.days.length - 1]) {
        weeks.push(current);
        current = [];
      }
    }
    return weeks;
  };

  const weeks = getWeeks();
  const currentWeek = weeks[weekIndex] || [];

  // Compliance summary
  const getComplianceSummary = () => {
    if (!monthlyMenu?.days) return { total: 0, compliant: 0 };
    let total = 0;
    let compliant = 0;
    for (const day of monthlyMenu.days) {
      for (const mt of MEAL_TYPES) {
        const meal = day.meals?.[mt.id];
        if (meal?.name) {
          total++;
          if (isCompliant(mt.id, meal.components)) compliant++;
        }
      }
    }
    return { total, compliant };
  };

  const compliance = getComplianceSummary();

  const ComponentBadge = ({ component, value }) => {
    if (!value) return null;
    return (
      <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border ${CACFP_COLORS[component]}`}>
        {CACFP_LABELS[component]}: {value}
      </span>
    );
  };

  const ComplianceIndicator = ({ mealType, components }) => {
    const filled = countFilledComponents(components);
    const required = CACFP_REQUIREMENTS[mealType];
    const ok = filled >= required;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${ok ? 'text-green-600' : 'text-red-500'}`}>
        {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
        {filled}/{required}
      </span>
    );
  };

  return (
    <DashboardLayout title="Monthly Menu">
      <div className="space-y-6" data-testid="menu-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <UtensilsCrossed className="w-6 h-6 text-primary" />
              Monthly Menu
            </h1>
            <p className="text-muted-foreground mt-1">CACFP-compliant meal planning with AI generation</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {monthlyMenu && isSaved && (
              <Button variant="outline" onClick={handleExportPDF} disabled={loading} data-testid="export-pdf-btn">
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            )}
            {isStaff && monthlyMenu && (
              <>
                <Button variant="outline" onClick={handleDelete} data-testid="delete-menu-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={handleSave} disabled={loading || isSaved} data-testid="save-menu-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {isSaved ? 'Saved' : 'Save Menu'}
                </Button>
              </>
            )}
            {isStaff && (
              <Button className="bg-primary hover:bg-primary/90" onClick={() => { setGenerateMonth(selectedMonth); setShowGenerateDialog(true); }} data-testid="generate-menu-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                AI Generate Menu
              </Button>
            )}
          </div>
        </div>

        {/* Month Selector */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => {
                const [y, m] = selectedMonth.split('-').map(Number);
                const d = new Date(y, m - 2, 1);
                setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
              }}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="flex flex-wrap items-center justify-center gap-2 order-first w-full sm:order-none sm:w-auto">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-full sm:w-[220px]" data-testid="month-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedMonth === currentMonthStr && (
                  <Badge variant="secondary">Current Month</Badge>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                const [y, m] = selectedMonth.split('-').map(Number);
                const d = new Date(y, m, 1);
                setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
              }}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* CACFP Legend */}
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground mr-1">CACFP Components:</span>
              {CACFP_COMPONENTS.map(c => (
                <Badge key={c} variant="outline" className={`text-xs ${CACFP_COLORS[c]}`}>
                  {CACFP_LABELS[c]}
                </Badge>
              ))}
              <span className="text-xs text-muted-foreground ml-2">
                Breakfast: 3 | Snacks: 2 | Lunch & Dinner: 5
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Summary */}
        {monthlyMenu && compliance.total > 0 && (
          <Card className={compliance.compliant === compliance.total ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}>
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                {compliance.compliant === compliance.total ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                )}
                <span className="text-sm font-medium">
                  CACFP Compliance: {compliance.compliant}/{compliance.total} meals meet requirements
                  {compliance.compliant === compliance.total ? ' - All meals compliant!' : ''}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generation Progress */}
        {generating && generateProgress && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="py-6 text-center">
              <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
              <p className="font-medium text-primary">{generateProgress}</p>
              <p className="text-sm text-muted-foreground mt-1">This may take a minute. Each week is generated separately.</p>
            </CardContent>
          </Card>
        )}

        {/* No Menu State */}
        {!monthlyMenu && !loading && !generating && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <UtensilsCrossed className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Menu for This Month</h3>
              <p className="text-muted-foreground mb-4">
                Generate an AI-powered CACFP-compliant menu
              </p>
              {isStaff && (
                <Button onClick={() => { setGenerateMonth(selectedMonth); setShowGenerateDialog(true); }}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Monthly Menu
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {loading && !monthlyMenu && (
          <Card>
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading menu...</p>
            </CardContent>
          </Card>
        )}

        {/* Weekly View of Monthly Menu */}
        {monthlyMenu && weeks.length > 0 && (
          <>
            {/* Week Navigation */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="outline" size="sm" disabled={weekIndex === 0} onClick={() => setWeekIndex(weekIndex - 1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Prev Week
              </Button>
              <div className="flex flex-wrap items-center justify-center gap-2 order-first w-full sm:order-none sm:w-auto">
                {weeks.map((w, idx) => {
                  const todayIso = new Date().toISOString().split('T')[0];
                  const isCurrentWeek = w.some(d => d.date === todayIso);
                  return (
                    <Button
                      key={idx}
                      variant={idx === weekIndex ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWeekIndex(idx)}
                      data-testid={`week-btn-${idx}`}
                      className={isCurrentWeek && idx !== weekIndex ? 'ring-2 ring-primary/40 ring-offset-1' : ''}
                    >
                      Week {idx + 1}
                      {isCurrentWeek && <span className="ml-1 text-[10px] opacity-80">• This Week</span>}
                    </Button>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" disabled={weekIndex === weeks.length - 1} onClick={() => setWeekIndex(weekIndex + 1)}>
                Next Week
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Menu Grid */}
            <div className="overflow-x-auto">
              <div className="min-w-[900px]">
                {/* Day Headers */}
                <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: `100px repeat(${currentWeek.length}, 1fr)` }}>
                  <div className="p-2 text-sm font-medium text-muted-foreground">Meal</div>
                  {currentWeek.map((day, idx) => (
                    <div key={idx} className="p-2 text-center bg-primary/5 rounded-t-lg">
                      <div className="font-semibold text-sm">{day.day_name}</div>
                      <div className="text-xs text-muted-foreground">{day.date}</div>
                    </div>
                  ))}
                </div>

                {/* Meal Rows */}
                {MEAL_TYPES.map(mt => (
                  <div key={mt.id} className="grid gap-2 mb-2" style={{ gridTemplateColumns: `100px repeat(${currentWeek.length}, 1fr)` }}>
                    <div className="p-2 bg-muted/50 rounded-lg flex flex-col justify-center">
                      <span className="font-medium text-xs">{mt.label}</span>
                      <span className="text-[10px] text-muted-foreground">{mt.req} of 5 comp.</span>
                    </div>
                    {currentWeek.map((day, dayIdx) => {
                      const globalDayIdx = monthlyMenu.days.indexOf(day);
                      const meal = day.meals?.[mt.id];
                      const hasContent = meal?.name;

                      return (
                        <div
                          key={dayIdx}
                          className={`rounded-lg border p-2 min-h-[90px] transition-colors ${
                            hasContent
                              ? isCompliant(mt.id, meal?.components)
                                ? 'bg-white hover:bg-green-50/30 border-green-100'
                                : 'bg-white hover:bg-red-50/30 border-red-100'
                              : 'bg-muted/20 border-dashed border-muted-foreground/20'
                          } ${isStaff ? 'cursor-pointer hover:shadow-sm' : ''}`}
                          onClick={() => isStaff && openEditMeal(globalDayIdx, mt.id)}
                          data-testid={`meal-cell-${globalDayIdx}-${mt.id}`}
                        >
                          {hasContent ? (
                            <div className="space-y-1">
                              <div className="flex items-start justify-between gap-1">
                                <span className="font-medium text-xs leading-tight">{meal.name}</span>
                                <ComplianceIndicator mealType={mt.id} components={meal.components} />
                              </div>
                              <div className="flex flex-wrap gap-0.5">
                                {CACFP_COMPONENTS.map(comp =>
                                  meal.components?.[comp] ? (
                                    <ComponentBadge key={comp} component={comp} value={meal.components[comp]} />
                                  ) : null
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="h-full flex items-center justify-center text-muted-foreground/40">
                              {isStaff ? (
                                <span className="flex items-center text-xs"><Plus className="w-3 h-3 mr-1" />Add</span>
                              ) : (
                                <span className="text-xs">-</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Generate Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                AI Generate Monthly Menu
              </DialogTitle>
              <DialogDescription>
                Generate a full month of CACFP-compliant meals using AI
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={generateMonth} onValueChange={setGenerateMonth}>
                  <SelectTrigger data-testid="generate-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Special Dietary Notes (Optional)</Label>
                <Textarea
                  placeholder="e.g., No pork, include more vegetarian options, avoid nuts..."
                  value={dietaryNotes}
                  onChange={(e) => setDietaryNotes(e.target.value)}
                  rows={3}
                  data-testid="dietary-notes-input"
                />
              </div>
              <Card className="bg-muted/30">
                <CardContent className="py-3">
                  <h4 className="text-sm font-medium mb-2">CACFP Requirements:</h4>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>Breakfast: 3 of 5 components (milk, protein, grains, fruits, vegetables)</p>
                    <p>AM Snack: 2 of 5 components</p>
                    <p>Lunch: All 5 components</p>
                    <p>PM Snack: 2 of 5 components</p>
                    <p>Dinner: All 5 components</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
              <Button onClick={handleGenerate} disabled={generating} data-testid="generate-submit-btn">
                {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {generating ? 'Generating...' : 'Generate Menu'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Meal Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-primary" />
                Edit Meal
              </DialogTitle>
              {editTarget && (
                <DialogDescription>
                  {MEAL_TYPES.find(m => m.id === editTarget.mealType)?.label} - {monthlyMenu?.days?.[editTarget.dayIndex]?.day_name} ({monthlyMenu?.days?.[editTarget.dayIndex]?.date})
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Meal Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="e.g., Oatmeal with Fresh Berries"
                  data-testid="edit-meal-name"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>CACFP Food Components</Label>
                  {editTarget && (
                    <ComplianceIndicator mealType={editTarget.mealType} components={editForm.components} />
                  )}
                </div>

                {CACFP_COMPONENTS.map(comp => (
                  <div key={comp} className="space-y-1">
                    <Label className={`text-xs ${CACFP_COLORS[comp].split(' ')[1]}`}>{CACFP_LABELS[comp]}</Label>
                    <Input
                      value={editForm.components[comp]}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        components: { ...editForm.components, [comp]: e.target.value }
                      })}
                      placeholder={`e.g., ${comp === 'milk' ? 'Whole Milk' : comp === 'protein' ? 'Grilled Chicken' : comp === 'grains' ? 'Whole Wheat Bread' : comp === 'fruits' ? 'Apple Slices' : 'Steamed Broccoli'}`}
                      className="h-9"
                      data-testid={`edit-component-${comp}`}
                    />
                  </div>
                ))}

                {editTarget && (
                  <p className="text-xs text-muted-foreground">
                    Required: {CACFP_REQUIREMENTS[editTarget.mealType]} of 5 components for {MEAL_TYPES.find(m => m.id === editTarget.mealType)?.label}
                    {editForm.components && !isCompliant(editTarget.mealType, editForm.components) && (
                      <span className="text-red-500 ml-1">
                        (Need {CACFP_REQUIREMENTS[editTarget.mealType] - countFilledComponents(editForm.components)} more)
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={saveEditMeal} data-testid="save-meal-edit-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Meal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MenuPage;
